import { baseAxiosConfig, requests } from "@/functional/api/api";
import type {
  PromiseResult,
  Success,
  TokenResponse,
} from "@passwordlessdev/passwordless-client";
import { Client } from "@passwordlessdev/passwordless-client";
import type PassKeyCredential from "@/models/passKeyCredential.model";
import type { UserLoginResponse } from "@/functional/api/apiUsers.ts";

const getClient = () => {
  return new Client({
    apiKey: import.meta.env.VITE_PASSWORDLESS_PUBLIC_KEY,
  });
};

const convertBase64ToUrlSafeBase64 = (base64String: string): string => {
  return base64String.replace(/\+/g, "-").replace(/\//g, "_");
};

const register = async (
  userId: string,
  alias: string,
  nickname: string,
): PromiseResult<TokenResponse> => {
  console.log("Registering MFA for user:", userId);
  const client = getClient();

  // Get a register token:
  let registerToken: string;
  try {
    registerToken = await requests.get<string>(`users/${userId}/create-token`, {
      alias,
    });
  } catch (e) {
    console.error("Failed to create token: ", e);
    return {
      error: {
        from: "create-token",
        errorCode: "TOKEN_CREATION_FAILED",
        title: "Failed to create registration token",
      },
    };
  }

  // Register token with user's device
  const tokenResponse = await client.register(registerToken, nickname);
  const { token, error } = tokenResponse;
  console.log("Register token used by client. Token:", token, "Error:", error);

  return tokenResponse;
};

const passKeySignIn = async (
  userId: string,
  alias: string,
): PromiseResult<UserLoginResponse> => {
  console.log("Signing in with MFA for user:", userId);
  const client = getClient();
  // Sign in by user id rather than with a 'discoverable 'credential, so that the
  // authenticator is only offered the credentials registered to this user in
  // this environment's Passwordless tenant
  const tokenResponse = await client.signinWithId(userId);
  const { token, error } = tokenResponse;
  console.log("Sign-in token used by client. Token:", token, "Error:", error);

  if (token === undefined) {
    console.error("Passwordless sign-in did not return a token: ", error);
    return {
      error: {
        from: "signin",
        errorCode: error?.errorCode ?? "SIGNIN_FAILED",
        title: error?.title ?? "Failed to sign in with your pass key",
      },
    };
  }

  // Verify the token:
  try {
    return (await requests.post<UserLoginResponse>(
      `users/${userId}/passwordless-login`,
      {
        alias,
        token,
      },
    )) as Success<UserLoginResponse>;
  } catch (e) {
    console.error("Failed to verify token: ", e);
    return {
      error: {
        from: "passwordless-login",
        errorCode: "PASSWORDLESS_LOGIN_FAILED",
        title: "Failed to verify sign-in token",
      },
    };
  }
};

const listCredentials = async (
  userId: string,
): Promise<PassKeyCredential[]> => {
  console.log("Listing credentials for user:", userId);
  try {
    return await requests.get<PassKeyCredential[]>(
      `users/${userId}/credentials`,
    );
  } catch (e) {
    console.error("Failed to list credentials: ", e);
    throw e;
  }
};

const deleteCredential = async (
  userId: string,
  credentialId: string,
): Promise<void> => {
  // Passwordless returns credential ids in 'standard base 64' rather than 'url-safe base 64'
  // which is what the API is expecting for deletion
  const decodedCredentialId = convertBase64ToUrlSafeBase64(credentialId);

  try {
    await requests.delete(`users/${userId}/credentials`, undefined, {
      ...baseAxiosConfig({ credentialId: decodedCredentialId }),
    });
  } catch (e) {
    console.error(`Failed to delete credential (${decodedCredentialId}): `, e);
    throw e;
  }
};

export default {
  register,
  passKeySignIn,
  listCredentials,
  deleteCredential,
};
